import { AutoFixHigh, Edit, MoreHoriz, PublishedWithChanges } from '@mui/icons-material';
import { Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemThumbnail } from '~/components/ItemThumbnail';
import { ActionButtonLink } from '~/components/button';
import { hiddenHeader } from '~/components/data-grid';
import { FolderIcon } from '~/components/folder-breadcrumbs/styles';
import { useLink } from '~/hooks/link';
import { formatDate } from '~/lib/datetime';
import type {
  DesignList__Design as Design,
  DesignList__DesignFolder as Folder,
} from '../DesignList.generated';
import { StudioLocation, StudioMenu } from '../components';

type DesignOrFolder = Design | Folder;

export const useColumns = ({
  search = false,
}: {
  search: boolean;
}): GridColDef<DesignOrFolder>[] => {
  const { t } = useTranslation();
  const link = useLink();
  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));
  return useMemo(() => {
    const columns: GridColDef<DesignOrFolder>[] = [
      {
        field: 'thumbnailUri',
        ...hiddenHeader(t('common.labels.thumbnail')),
        width: 84,
        resizable: false,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.__typename === 'DesignFolder') {
            return (
              <Box display="flex" alignItems="center" justifyContent="center" width="100%">
                <FolderIcon />
              </Box>
            );
          }
          return (
            <Box display="flex" alignItems="center" justifyContent="center" width="100%">
              <ItemThumbnail
                thumbnailUri={row.thumbnailUri}
                fallbackIcon={<AutoFixHigh sx={{ color: 'text.secondary', fontSize: 20 }} />}
              />
            </Box>
          );
        },
      },
      {
        field: 'name',
        flex: 1,
        headerName: t('common.labels.name'),
        minWidth: isSmallAndUp ? 180 : 150,
      },
      {
        field: 'designCount',
        headerName: t('pages.studio.list.columns.designs'),
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          if (row.__typename === 'DesignFolder') {
            return <Chip color="primary" label={row.itemCount || 0} />;
          }
          return '--';
        },
        minWidth: 150,
      },
      {
        field: 'publishedApp',
        ...hiddenHeader(t('common.labels.status')),
        minWidth: 120,
        resizable: false,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.__typename === 'DesignFolder') return null;
          return row.publishedApp ? (
            <Chip
              icon={<PublishedWithChanges />}
              label={t('pages.studio.list.columns.published')}
              color="success"
            />
          ) : null;
        },
      },
      {
        field: 'updatedAt',
        headerName: t('common.labels.updated_at'),
        minWidth: 250,
        sortable: false,
        valueFormatter: (value: Date) => formatDate(value),
      },
    ];

    search
      ? columns.push({
          field: 'location',
          headerName: t('pages.studio.list.columns.location'),
          minWidth: 250,
          renderCell: ({ row }) => {
            if (row.__typename === 'DesignFolder') return null;
            return <StudioLocation designFolderId={row.designFolderId} />;
          },
          sortable: false,
        })
      : columns.push({
          field: 'createdAt',
          headerName: t('common.labels.created'),
          minWidth: 250,
          sortable: false,
          renderCell: ({ row }) => {
            return formatDate(row.createdAt);
          },
        });

    columns.push({
      field: 'actions',
      type: 'actions',
      align: 'right',
      minWidth: isSmallAndUp ? 100 : 0,
      getActions: ({ row }) => {
        const actions = [];

        if (row.__typename === 'DesignFolder') {
          return [<StudioMenu key={`more-${row.id}`} item={row} Icon={MoreHoriz} />];
        }

        actions.push(
          <ActionButtonLink
            key={`edit-${row.id}`}
            title={t('common.actions.edit')}
            Icon={Edit}
            to={link(`/studio/${row.designFolderId}/design/${row.id}`)}
          />,
          <StudioMenu key={`more-${row.id}`} item={row} Icon={MoreHoriz} />,
        );

        return actions;
      },
    });

    return columns;
  }, [isSmallAndUp, link, search, t]);
};
